import * as React from "react";
import { cn } from "../../lib/utils";

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  className?: string;
  side?: "top" | "bottom" | "left" | "right";
  immediate?: boolean; // Show immediately (for icons) vs with delay (for text)
  delayLong?: boolean; // Show with 2-second delay (for main canvas elements)
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, className, side = "top", immediate = false, delayLong = false }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const showDelay = immediate ? 0 : delayLong ? 2000 : 700;

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (showDelay === 0) {
      setIsVisible(true);
    } else {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, showDelay);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getTooltipClasses = () => {
    const visibilityClasses = isVisible ? "visible opacity-100" : "invisible opacity-0";
    const baseClasses = `${visibilityClasses} absolute z-[9999] px-3 py-2 text-xs bg-popover text-popover-foreground border rounded shadow-lg w-64 leading-relaxed transition-opacity duration-200`;
    
    switch (side) {
      case "top":
        return cn(baseClasses, "-top-2 left-1/2 transform -translate-x-1/2 -translate-y-full");
      case "bottom":
        return cn(baseClasses, "top-full mt-2 left-1/2 transform -translate-x-1/2");
      case "left":
        return cn(baseClasses, "right-full mr-2 top-1/2 transform -translate-y-1/2");
      case "right":
        return cn(baseClasses, "left-full ml-2 top-1/2 transform -translate-y-1/2");
      default:
        return cn(baseClasses, "-top-2 left-1/2 transform -translate-x-1/2 -translate-y-full");
    }
  };

  return (
    <div 
      className={cn("relative", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <div className={getTooltipClasses()}>
        {content}
      </div>
    </div>
  );
};

export { Tooltip }; 